<template>
  <v-dialog
    v-model="configurator.showCreateNewProjectDialog"
    max-width="600"
    :loading="configurator.currentProjectLoading"
    class="roundedDialog"
  >
    <v-card
      width="714"
      height="903"
      loader-height="4"
      color="white"
      class="sigfloCard"
      :loading="configurator.currentProjectLoading"
      :disabled="configurator.currentProjectLoading"
    >
      <v-form ref="createProjectForm" submit-prevent>
        <v-card-title class="sigfloBodyLarge justify-center cardTitle mb-6"
          >Start New Project
          <v-btn class="closeDialogButton" plain icon @click="closeCreateNewProjectDialog">
            <v-icon>mdi-close</v-icon></v-btn
          >
        </v-card-title>
        <v-card-text class="px-12">
          <v-row class="mt-2">
            <span class="sigfloBodySmall">Project name*</span>
          </v-row>
          <v-row>
            <v-text-field
              color="#BCB2CD"
              autofocus
              outlined
              label="Project name"
              single-line
              hint="The Project Name will identify a group of audio, video, and lighting systems in a single project"
              v-model="name"
              :rules="[...validation.length150, ...validation.required]"
              @input="validateForm"
            ></v-text-field>
          </v-row>
          <div v-for="setting in configurator.projectSettingDefinitions" :key="setting.name">
            <v-row class="mt-2">
              <span class="sigfloBodySmall">{{ setting.name }}</span>
            </v-row>
            <v-row>
              <v-text-field
                v-if="setting.controlType.toLowerCase() === 'int'"
                outlined
                single-line
                v-model="setting.value"
                :rules="validation.intRule"
                :suffix="setting.valueSuffix"
                :label="setting.label"
                value=""
                :hint="setting.description"
                @input="validateForm"
              ></v-text-field>
              <v-text-field
                v-if="setting.controlType.toLowerCase() === 'multidecimal'"
                outlined
                single-line
                v-model="setting.value"
                :rules="validation.decRule"
                :suffix="setting.valueSuffix"
                :label="setting.name"
                value=""
                :hint="setting.description"
                @input="validateForm"
              ></v-text-field>
              <v-select
                v-if="setting.controlType.toLowerCase() === 'dropdownlist'"
                outlined
                single-line
                v-model="setting.value"
                persistent-hint
                :hint="setting.description"
                :items="getProjectSettingValueModels(setting)"
                content-class="elevation-10"
                item-text="label"
                item-value="value"
                :label="setting.name"
                @input="validateForm"
              ></v-select>
              <v-select
                v-if="setting.controlType.toLowerCase() === 'multiselect'"
                outlined
                single-line
                :value="setting.value ? setting.value.split(',') : null"
                :items="setting.projectSettingValueModels"
                attach
                chips
                :label="setting.label"
                :hint="setting.description"
                multiple
                item-text="label"
                item-value="value"
                :rules="validation.selection"
                deletable-chips
                @change="updateProjectSettingModel($event, setting)"
                @input="validateForm"
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip
                    :key="index"
                    close
                    @click:close="removeItem(item, index)"
                    class="custom-chip"
                    color="#bcb2cd"
                    dark
                  >
                    {{ item.value }}
                  </v-chip>
                </template>
              </v-select>
              <v-checkbox v-if="setting.controlType.toLowerCase() === 'yesno'" v-model="setting.value"></v-checkbox>
            </v-row>
          </div>
          <v-row class="mt-2">
            <v-col cols="6" class="py-0 pr-1">
              <span class="sigfloBodySmall">Budget</span>
            </v-col>
            <v-col cols="6" class="pa-0 pl-1">
              <span class="sigfloBodySmall">Install Date</span>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="pa-0 pr-1">
              <v-text-field
                width="200"
                color="#BCB2CD"
                outlined
                single-line
                label="Budget"
                v-model="formattedBudget"
                :rules="validation.numeric"
                hint="Budget for this project"
              ></v-text-field>
            </v-col>
            <v-col cols="6" class="pa-0 pl-1">
              <v-menu
                ref="completionDateMenu"
                v-model="completionDateMenu"
                :close-on-content-click="true"
                transition="scale-transition"
                label="Install Date"
                offset-y
                max-width="290px"
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    outlined
                    color="#BCB2CD"
                    v-model="completionDate"
                    label=""
                    hint="Planned installation date"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="completionDate"
                  dense
                  dark
                  color="primary"
                  no-title
                  elevation="10"
                  class="elevation-10"
                  @input="menu1 = false"
                  prev-icon="mdi-skip-previous"
                  next-icon="mdi-skip-next"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>

          <v-row class="mt-2">
            <span class="sigfloBodySmall">Notes</span>
          </v-row>
          <v-row>
            <v-textarea
              outlined
              label="Type here..."
              single-line
              v-model="notes"
              hint="Enter notes about your project here"
            ></v-textarea>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize sigfloButton mr-6 px-6 sigfloGreenButton"
            color="#32FF98"
            rounded
            elevation="0"
            :disabled="!isProjectFormValid"
            @click="saveNewProject"
            >Create Project<v-icon class="pl-2">mdi-arrow-right</v-icon></v-btn
          >
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
  import { mapState } from 'vuex';

  export default {
    data() {
      return {
        completionDateMenu: false,
        completionDate: null,
        name: null,
        budget: 0,
        notes: null,
        settingsModel: [],
        formValid: false
      };
    },
    created() {
      this.settingsModel = this.configurator.projectSettingDefinitions;
    },
    methods: {
      /**
       * Validates the form by calling the `validate` method on the `createProjectForm` ref.
       */
      validateForm() {
        this.formValid = this.$refs.createProjectForm.validate();
      },
      /**
       * Removes an item from the setting value.
       *
       * @param {Object} setting - The setting object.
       * @param {number} index - The index of the item to remove.
       */
      removeItem(setting, index) {
        setting.value = setting.value.split(',').splice(index, 1).join(',');
      },
      /**
       * Updates datamodel for selected record
       */
      updateProjectSettingModel: function (newValue, selectedRecord) {
        // convert multiselect data to delimited string
        if (Array.isArray(newValue)) newValue = newValue.join(',');
        selectedRecord.value = newValue;
      },
      /**
       * Closes the create new project dialog.
       */
      closeCreateNewProjectDialog: function () {
        this.$store.commit('configurator/showCreateNewProjectDialog', false);
      },
      /**
       * Builds the dropdown list objects for specified setting
       */
      getProjectSettingValueModels: function (setting) {
        var items = [...setting.projectSettingValueModels];
        return items;
      },
      /**
       * Saves a new project.
       *
       * This method validates the form inputs and creates a new project object with the provided data.
       * It dispatches an action to the Vuex store to create the project, and upon successful creation,
       * it closes the create project dialog, retrieves the updated list of projects, and opens the newly
       * created project.
       *
       * @returns {void}
       */
      saveNewProject: function () {
        if (this.$refs.createProjectForm.validate()) {
          let newProject = {
            name: this.name,
            budget: Number(this.budget),
            completionDate: this.completionDate ? new Date(this.completionDate).toISOString().slice(0, -1) : null,
            notes: this.notes,
            projectSettingModels: [...this.configurator.projectSettingDefinitions]
          };
          this.$store.commit('configurator/setCurrentProjectLoading', true);
          this.$store.dispatch('configurator/createProject', newProject).then((project) => {
            this.resetValues();
            this.$store.commit('configurator/setCurrentProjectLoading', false);
            if (project.sigMessageModel) {
              this.processSigResponse(project.sigMessageModel);
            }
            this.closeCreateNewProjectDialog();
            this.$store.dispatch('configurator/getProjects');
            this.openProject(project.id);
            // reset default project settings
            this.$store.dispatch('configurator/getProjectSettingDefinitions');
          });
        }
      },
      /**
       * Resets the values of the project form fields.
       */
      resetValues() {
        this.name = null;
        this.budget = 0;
        this.completionDate = null;
        this.notes = null;
        this.settingsModel = [];
      }
    },
    watch: {
      'configurator.showCreateNewProjectDialog': function (value) {
        if (value) {
          this.resetValues();
        }
      }
    },
    computed: {
      ...mapState(['app', 'navigation', 'configurator']),
      formattedBudget: {
        get() {
          if (this.budget === '') return '';
          return this.budget.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
        set(value) {
          this.budget = value.replace(/,/g, '');
        }
      },
      isProjectFormValid() {
        return this.formValid;
      }
    }
  };
</script>

<style>
  .theme--light.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
    border: 1px solid #f9c8ce !important;
    background-color: #fdedef !important;
    color: #ef6c7c !important;
  }
  .closeDialogButton {
    position: absolute;
    right: 10px;
    top: 10px;
  }
  .cardTitle {
    border-bottom: 1px solid #bcb2cd;
  }
</style>
